import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Barbell, ShieldCheck, Spinner, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForceChangePassword() {
  const { user, setUser, logout } = useAuth();
  const [tempPwd, setTempPwd] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: tempPwd, new_password: pwd });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Password updated. Welcome in!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6 py-12" data-testid="force-change-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10">
          <Barbell size={28} weight="fill" color="#FF3B30" />
          <span className="font-head text-2xl font-extrabold uppercase tracking-tight">Athlete<span className="text-[#FF3B30]">Hub</span></span>
        </div>
        <div className="w-14 h-14 rounded-sm bg-[#FF3B30]/15 flex items-center justify-center mb-6"><ShieldCheck size={30} weight="fill" color="#FF3B30" /></div>
        <h1 className="font-head text-4xl font-black uppercase tracking-tighter leading-none mb-2">Secure your account</h1>
        <p className="text-[#A3A3A3] mb-8">Welcome, {user?.name?.split(" ")[0]}. For security, please set a new password before continuing.</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Current (temporary) password</label>
            <input type="password" value={tempPwd} onChange={(e) => setTempPwd(e.target.value)} required data-testid="fcp-current-input"
              className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="••••••••" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">New password</label>
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required data-testid="fcp-new-input"
              className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2">Confirm new password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="fcp-confirm-input"
              className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors" placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} data-testid="fcp-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-60 text-white font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-colors">
            {loading ? <Spinner size={20} className="animate-spin" /> : <>Set password & continue</>}
          </button>
        </form>

        <button onClick={logout} data-testid="fcp-logout-button" className="mt-8 inline-flex items-center gap-2 text-sm text-[#737373] hover:text-white transition-colors">
          <SignOut size={16} weight="bold" /> Sign out
        </button>
      </div>
    </div>
  );
}
